<template>
  <div class="about-us">
    <!-- 第一板块：公司介绍 -->
    <section class="company-intro bg-white py-16 lg:py-24">
      <div class="max-w-7xl mx-auto px-4 lg:px-8">
        <!-- 标题区域 -->
        <div class="text-center mb-12 lg:mb-16">
          <h1 class="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            {{ content.title }}
          </h1>
          <div class="w-20 h-1 bg-hongtu-primary mx-auto rounded-full"></div>
        </div>

        <!-- 公司介绍内容 -->
        <div class="max-w-4xl mx-auto">
          <div class="prose prose-lg max-w-none">
            <p
              v-for="(paragraph, index) in content.introParagraphs"
              :key="`intro-${index}`"
              :class="[
                'text-gray-700 leading-relaxed text-base lg:text-lg',
                index < content.introParagraphs.length - 1 ? 'mb-6' : ''
              ]"
            >
              {{ paragraph }}
            </p>
          </div>

          <!-- 牌照展示 -->
          <div class="mt-12 lg:mt-16">
            <h3 class="text-xl font-bold text-gray-900 mb-8 text-center">{{ content.licenseTitle }}</h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div
                v-for="license in content.licenses"
                :key="license.number"
                class="license-card bg-gray-50 rounded-lg p-6 text-center border border-gray-100 hover:shadow-lg transition-shadow"
              >
                <div class="w-16 h-16 bg-hongtu-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span class="text-2xl font-bold text-hongtu-primary">{{ license.number }}</span>
                </div>
                <h4 class="font-semibold text-gray-900 mb-2">{{ license.title }}</h4>
                <p class="text-sm text-gray-600">{{ license.subtitle }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 第二板块：发展历程 -->
    <section class="company-history bg-gray-50 py-16 lg:py-24">
      <div class="max-w-7xl mx-auto px-4 lg:px-8">
        <!-- 标题区域 -->
        <div class="text-center mb-12 lg:mb-16">
          <h2 class="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            {{ content.historyTitle }}
          </h2>
          <div class="w-20 h-1 bg-hongtu-primary mx-auto rounded-full"></div>
        </div>

        <!-- 时间线 -->
        <div class="max-w-4xl mx-auto">
          <div class="timeline relative">
            <!-- 时间线中轴线 -->
            <div class="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-gradient-to-b from-hongtu-primary/30 via-hongtu-primary to-hongtu-primary/30"></div>

            <!-- 时间线项目 -->
            <div class="space-y-8 lg:space-y-12">
              <div v-for="(milestone, index) in content.milestones" :key="`${milestone.date}-${index}`" class="timeline-item relative">
                <div class="md:flex items-center">
                  <div v-if="isLeft(index)" class="md:w-1/2 md:pr-12 md:text-right mb-4 md:mb-0">
                    <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                      <span class="inline-block px-3 py-1 bg-hongtu-primary text-white text-sm font-semibold rounded-full mb-2">{{ milestone.date }}</span>
                      <p class="text-gray-700">{{ milestone.content }}</p>
                    </div>
                  </div>
                  <div v-else class="md:w-1/2 md:pr-12"></div>
                  <div class="hidden md:flex absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-hongtu-primary rounded-full border-4 border-white shadow"></div>
                  <div v-if="!isLeft(index)" class="md:w-1/2 md:pl-12 mb-4 md:mb-0">
                    <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                      <span class="inline-block px-3 py-1 bg-hongtu-primary text-white text-sm font-semibold rounded-full mb-2">{{ milestone.date }}</span>
                      <p class="text-gray-700">{{ milestone.content }}</p>
                    </div>
                  </div>
                  <div v-else class="md:w-1/2 md:pl-12"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { normalizeLocale } from '../services/contentService'

const ABOUT_CONTENT = {
  zh: {
    title: '关于红土',
    introParagraphs: [
      '红土证券于2022年在香港注册成立，公司持有香港证监会颁发的第1类（证券交易）、第4类（就证券提供意见）、第9类（提供资产管理）牌照。公司在香港设有办公室，可为客户提供全球化资产配置和海内外基金运作方案。公司历史投资成绩优异，致力于成为宏观对冲大类资产配置的顶级资产管理机构，在不同的经济周期内穿越牛熊，为客户创造持续稳定的绝对收益。',
      '公司采取全球宏观对冲大类资产配置投资策略，核心逻辑立足于自上而下的宏观研判。通过深入分析全球经济周期、货币与财政政策走向、地缘政治事件以及国际资本流动趋势，识别大类资产（包括但不限于股票、债券、利率、外汇、大宗商品、虚拟资产及衍生品）在不同市场环境下的定价偏差。灵活运用多空双向交易、跨资产套利及杠杆调节等手段，在全球范围内进行多元化资产配置，旨在有效规避单一市场系统性风险，力求在不同的经济周期中实现与传统市场指数低相关性的长期稳健绝对收益。'
    ],
    licenseTitle: '持牌资质',
    licenses: [
      { number: '1', title: '第1类牌照', subtitle: '证券交易' },
      { number: '4', title: '第4类牌照', subtitle: '就证券提供意见' },
      { number: '9', title: '第9类牌照', subtitle: '提供资产管理' }
    ],
    historyTitle: '发展历程',
    milestones: [
      { date: '2022年6月', content: '红土证券于香港注册成立' },
      { date: '2023年3月', content: '香港证监会颁发第4类（就证券提供意见）、第9类（提供资产管理）牌照' },
      { date: '2023年9月', content: '红土中国固定收益基金成立' },
      { date: '2024年2月', content: '香港证监会颁发第1类（证券交易）牌照' },
      { date: '2024年12月', content: '「中资海外基金新智奖」评选中，获TOP债券策略基金大奖、年度金牌基金经理奖' },
      { date: '2025年3月', content: '「中资离岸金久期」评选中，获卓越海外固收基金奖' },
      { date: '2025年4月', content: '红土稳健固定收益基金成立' },
      { date: '2025年12月', content: '「中资海外基金新智奖」评选中，获明星固定收益策略基金奖、TOP固定收益策略基金奖、年度金牌基金经理奖' },
      { date: '2026年2月', content: '红土全球宏观对冲基金成立' },
      { date: '2026年2月', content: '「介甫奖」评选中，获全球宏观策略年度最佳基金经理奖' }
    ]
  },
  'zh-Hant': {
    title: '關於紅土',
    introParagraphs: [
      '紅土證券於2022年在香港註冊成立，公司持有香港證監會頒發嘅第1類（證券交易）、第4類（就證券提供意見）、第9類（提供資產管理）牌照。公司在香港設有辦公室，可為客戶提供全球化資產配置同海內外基金運作方案。公司歷史投資成績優異，致力於成為宏觀對沖大類資產配置嘅頂級資產管理機構，喺唔同嘅經濟週期內穿越牛熊，為客戶創造持續穩定嘅絕對收益。',
      '公司採取全球宏觀對沖大類資產配置投資策略，核心邏輯立足於自上而下嘅宏觀研判。透過深入分析全球經濟週期、貨幣與財政政策走向、地緣政治事件以及國際資本流動趨勢，識別大類資產（包括但不限於股票、債券、利率、外匯、大宗商品、虛擬資產及衍生品）喺唔同市場環境下嘅定價偏差。靈活運用多空雙向交易、跨資產套利及槓桿調節等手段，喺全球範圍內進行多元化資產配置，旨在有效規避單一市場系統性風險，力求喺唔同嘅經濟週期中實現與傳統市場指數低相關性嘅長期穩健絕對收益。'
    ],
    licenseTitle: '持牌資質',
    licenses: [
      { number: '1', title: '第1類牌照', subtitle: '證券交易' },
      { number: '4', title: '第4類牌照', subtitle: '就證券提供意見' },
      { number: '9', title: '第9類牌照', subtitle: '提供資產管理' }
    ],
    historyTitle: '發展歷程',
    milestones: [
      { date: '2022年6月', content: '紅土證券於香港註冊成立' },
      { date: '2023年3月', content: '香港證監會頒發第4類（就證券提供意見）、第9類（提供資產管理）牌照' },
      { date: '2023年9月', content: '紅土中國固定收益基金成立' },
      { date: '2024年2月', content: '香港證監會頒發第1類（證券交易）牌照' },
      { date: '2024年12月', content: '「中資海外基金新智獎」評選中，獲TOP債券策略基金大獎、年度金牌基金經理獎' },
      { date: '2025年3月', content: '「中資離岸金久期」評選中，獲卓越海外固收基金獎' },
      { date: '2025年4月', content: '紅土穩健固定收益基金成立' },
      { date: '2025年12月', content: '「中資海外基金新智獎」評選中，獲明星固定收益策略基金獎、TOP固定收益策略基金獎、年度金牌基金經理獎' },
      { date: '2026年2月', content: '紅土全球宏觀對沖基金成立' },
      { date: '2026年2月', content: '「介甫獎」評選中，獲全球宏觀策略年度最佳基金經理獎' }
    ]
  },
  en: {
    title: 'About Redland Securities',
    introParagraphs: [
      'Redland Securities was incorporated in Hong Kong in 2022. The company holds Type 1 (Dealing in Securities), Type 4 (Advising on Securities), and Type 9 (Asset Management) licenses issued by the Securities and Futures Commission of Hong Kong. With offices in Hong Kong, the company provides clients with global asset allocation solutions and domestic and overseas fund management services. With an outstanding historical investment track record, the company is committed to becoming a top-tier asset management institution specializing in macro-hedged multi-asset class allocation, navigating through bull and bear markets across different economic cycles to generate consistent and stable absolute returns for clients.',
      'The company adopts a global macro-hedged multi-asset class allocation investment strategy, with its core logic grounded in top-down macroeconomic analysis. Through in-depth analysis of global economic cycles, monetary and fiscal policy trends, geopolitical events, and international capital flow patterns, the company identifies pricing discrepancies in major asset classes (including but not limited to equities, bonds, interest rates, foreign exchange, commodities, virtual assets, and derivatives) across different market environments. By flexibly employing long-short bilateral trading, cross-asset arbitrage, and leverage adjustment techniques, the company conducts diversified asset allocation on a global scale, aiming to effectively mitigate single-market systematic risks and strive to achieve long-term stable absolute returns with low correlation to traditional market indices across different economic cycles.'
    ],
    licenseTitle: 'Licenses & Qualifications',
    licenses: [
      { number: '1', title: 'Type 1 License', subtitle: 'Dealing in Securities' },
      { number: '4', title: 'Type 4 License', subtitle: 'Advising on Securities' },
      { number: '9', title: 'Type 9 License', subtitle: 'Asset Management' }
    ],
    historyTitle: 'Milestones',
    milestones: [
      { date: 'June 2022', content: 'Redland Securities was incorporated in Hong Kong' },
      { date: 'March 2023', content: 'Granted Type 4 (Advising on Securities) and Type 9 (Asset Management) licenses by the SFC' },
      { date: 'September 2023', content: 'Redland China Fixed Income Fund was established' },
      { date: 'February 2024', content: 'Granted Type 1 (Dealing in Securities) license by the SFC' },
      { date: 'December 2024', content: 'Awarded TOP Bond Strategy Fund Award and Annual Gold Fund Manager Award at the "China Overseas Fund New Intelligence Awards"' },
      { date: 'March 2025', content: 'Awarded Excellence in Overseas Fixed Income Fund Award at the "China Offshore Golden Duration Awards"' },
      { date: 'April 2025', content: 'Redland Stable Fixed Income Fund was established' },
      { date: 'December 2025', content: 'Awarded Star Fixed Income Strategy Fund Award, TOP Fixed Income Strategy Fund Award, and Annual Gold Fund Manager Award at the "China Overseas Fund New Intelligence Awards"' },
      { date: 'February 2026', content: 'Redland Global Macro Hedge Fund was established' },
      { date: 'February 2026', content: 'Awarded Global Macro Strategy Fund Manager of the Year at the "Jie Fu Awards"' }
    ]
  }
}

export default {
  name: 'AboutUs',
  computed: {
    localeKey() {
      const locale = normalizeLocale(this.$i18n ? this.$i18n.locale : 'zh')
      if (locale === 'en') return 'en'
      if (locale === 'zh-Hant') return 'zh-Hant'
      return 'zh'
    },
    content() {
      return ABOUT_CONTENT[this.localeKey] || ABOUT_CONTENT.zh
    }
  },
  methods: {
    isLeft(index) {
      return index % 2 === 0
    }
  }
}
</script>

<style scoped>
/* 移动端时间线样式 */
@media (max-width: 768px) {
  .timeline::before {
    content: '';
    position: absolute;
    left: 20px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(to bottom, rgba(233, 78, 27, 0.3), #e94e1b, rgba(233, 78, 27, 0.3));
  }

  .timeline-item {
    padding-left: 48px;
    position: relative;
  }

  .timeline-item::before {
    content: '';
    position: absolute;
    left: 12px;
    top: 24px;
    width: 16px;
    height: 16px;
    background: #e94e1b;
    border-radius: 50%;
    border: 4px solid white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

/* 牌照卡片悬停效果 */
.license-card {
  transition: all 0.3s ease;
}

.license-card:hover {
  transform: translateY(-4px);
}

/* 时间线项目动画 */
.timeline-item {
  opacity: 0;
  animation: fadeInUp 0.6s ease forwards;
}

.timeline-item:nth-child(1) { animation-delay: 0.1s; }
.timeline-item:nth-child(2) { animation-delay: 0.2s; }
.timeline-item:nth-child(3) { animation-delay: 0.3s; }
.timeline-item:nth-child(4) { animation-delay: 0.4s; }
.timeline-item:nth-child(5) { animation-delay: 0.5s; }
.timeline-item:nth-child(6) { animation-delay: 0.6s; }
.timeline-item:nth-child(7) { animation-delay: 0.7s; }
.timeline-item:nth-child(8) { animation-delay: 0.8s; }
.timeline-item:nth-child(9) { animation-delay: 0.9s; }
.timeline-item:nth-child(10) { animation-delay: 1s; }

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
